'use client';

import { useProject } from '@/contexts/ProjectContext';
import { formatDateRange } from '@/utils/dateFormatters';

interface SprintHeaderBarProps {
  sprint: {
    id: number;
    name: string;
    start_date: string | null;
    end_date: string | null;
    total_possible_hours: number;
  };
  tasks: Array<{
    estimated_hours: number | null;
    actual_hours: number | null;
  }>;
}

export default function SprintHeaderBar({ sprint, tasks }: SprintHeaderBarProps) {
  const { setSelectedSprint } = useProject();

  const calculateDaysLeft = (endDate: string | null) => {
    if (!endDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);
    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysLeft = calculateDaysLeft(sprint.end_date);
  const capacityTotal = sprint.total_possible_hours || 0;
  const estimatedHours = tasks.reduce((sum, task) => sum + (task.estimated_hours || 0), 0);
  const hoursWorked = tasks.reduce((sum, task) => sum + (task.actual_hours || 0), 0);
  const capacityUsed = estimatedHours;
  const capacityLeft = capacityTotal - capacityUsed;
  const capacityPercent = capacityTotal > 0 ? (capacityUsed / capacityTotal) * 100 : 0;

  return (
    <div className="flex items-center gap-6 flex-wrap px-6 py-4 border-b border-slate-700/50 bg-slate-800/40 backdrop-blur-sm flex-shrink-0">
      {/* Date Range */}
      <div className="flex items-center gap-2">
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <div className="flex items-center gap-1.5">
          <span className="text-white text-xl font-semibold">{formatDateRange(sprint.start_date, sprint.end_date, 'short')}</span>
          {daysLeft !== null && (
            <span className="text-amber-500 text-[10px] font-medium mt-0.5">
              {daysLeft > 0 ? `${daysLeft} Days Left` : daysLeft === 0 ? 'Today' : `${Math.abs(daysLeft)} Days Overdue`}
            </span>
          )}
        </div>
      </div>

      {/* Capacity Section */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-gray-400 text-sm">Capacity:</span>
          <span className="text-white text-sm">{capacityTotal.toFixed(0)} ({capacityLeft.toFixed(2)} left)</span>
        </div>
        {/* Capacity Bar */}
        <div className="w-40 bg-slate-700 rounded-full h-3">
          <div 
            className="bg-gradient-to-r from-amber-500 to-amber-600 h-3 rounded-full transition-all"
            style={{ width: `${Math.min(capacityPercent, 100)}%` }}
          />
        </div>
      </div>

      {/* Hours */}
      <div className="flex items-center gap-4 text-sm">
        <div>
          <span className="text-gray-400">Estimated:</span>
          <span className="text-white ml-1">{estimatedHours.toFixed(0)}h</span>
        </div>
        <div>
          <span className="text-gray-400">Hours worked:</span>
          <span className="text-white ml-1">{hoursWorked.toFixed(0)}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 ml-auto">
        {/* Member Filter */}
        <div>
          <label className="text-gray-500 text-xs block mb-1">Member:</label>
          <div className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded px-3 py-1.5 min-w-[140px]">
            <div className="w-5 h-5 rounded-full bg-slate-900 flex items-center justify-center">
              <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="text-gray-300 text-sm flex-1">All Members</span>
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Label Filter */}
        <div>
          <label className="text-gray-500 text-xs block mb-1">Label:</label>
          <div className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded px-3 py-1.5 min-w-[120px]">
            <span className="text-gray-300 text-sm flex-1">All Labels</span>
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Close Kanban Board Button */}
      <button
        onClick={() => setSelectedSprint(null)}
        className="ml-auto text-gray-400 hover:text-gray-300 p-1"
        title="Close kanban board"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

